import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { statusAPI, webmasterAuditAPI } from '../api/client'
import { useToast } from '../components/Toast'
import {
  Upload,
  RefreshCw,
  FileSearch,
  CheckCircle2,
  Loader2,
  AlertCircle,
  CircleSlash,
  Wand2,
  Clock3,
} from 'lucide-react'

const ISSUE_OPTIONS = [
  { value: 'duplicate_title', label: 'Одинаковые title' },
  { value: 'duplicate_description', label: 'Одинаковые description' },
]

const ISSUE_LABELS = {
  duplicate_title: 'Одинаковые title',
  duplicate_description: 'Одинаковые description',
}

const ISSUE_SHORT_LABELS = {
  duplicate_title: 'title',
  duplicate_description: 'description',
}

const STATUS_META = {
  matched_done: { label: 'Готово', icon: CheckCircle2, className: 'text-green-400' },
  matched_pending: { label: 'В очереди', icon: Loader2, className: 'text-yellow-400' },
  matched_failed: { label: 'Ошибка', icon: AlertCircle, className: 'text-red-400' },
  matched_unknown: { label: 'Найдено, статус неясен', icon: FileSearch, className: 'text-slate-300' },
  not_found: { label: 'Не найдено', icon: CircleSlash, className: 'text-slate-400' },
  needs_regeneration: { label: 'Нужна регенерация', icon: Wand2, className: 'text-orange-400' },
}

const ORIGIN_LABELS = {
  legacy_local: 'Старые локальные',
  aggregator_external: 'Из агрегатора',
}

const AI_STATUS_LABELS = {
  none: 'Не запускалось',
  queued: 'В очереди',
  pending: 'В работе',
  leased: 'В работе',
  done: 'Готово',
  failed: 'Ошибка',
}

function buildLojsUrl(publicPath) {
  const value = String(publicPath || '').trim()
  if (!value) return ''
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value
  }
  return `https://legkie-otkrytki.ru${value.startsWith('/') ? value : `/${value}`}`
}

function StatusBadge({ status, count }) {
  const meta = STATUS_META[status] || { label: status, icon: FileSearch, className: 'text-slate-300' }
  const Icon = meta.icon
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} className={meta.className} />
        <span className="font-medium">{meta.label}</span>
      </div>
      <div className="text-2xl font-bold">{count || 0}</div>
    </div>
  )
}

function BatchRow({ batch, isActive, onSelect }) {
  return (
    <button
      onClick={() => onSelect(batch.id)}
      className={`w-full text-left p-4 rounded-xl border transition-colors ${
        isActive ? 'bg-white/10 border-cyan-400/50' : 'bg-white/5 border-white/10 hover:bg-white/8'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold">{batch.uploaded_filename}</div>
          <div className="text-sm opacity-60 mt-1">
            Загружен: {new Date(batch.uploaded_at).toLocaleString()}
          </div>
        </div>
        <div className="text-right text-sm opacity-70">
          <div>{batch.total_urls} URL</div>
          <div>{batch.total_clusters} кластеров</div>
        </div>
      </div>
      <div className="flex gap-3 mt-3 text-sm opacity-80">
        <span>Сопоставлено: {batch.matched_total || 0}</span>
        <span>Готово: {batch.status_counts?.matched_done || 0}</span>
        <span>В очереди: {batch.status_counts?.matched_pending || 0}</span>
        <span>Не найдено: {batch.status_counts?.not_found || 0}</span>
      </div>
      <div className="mt-2 text-xs opacity-45">
        Источник отчёта: {ISSUE_SHORT_LABELS[batch.issue_type] || batch.issue_type}
      </div>
    </button>
  )
}

export default function WebmasterAudit() {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const [selectedBatchId, setSelectedBatchId] = useState(null)
  const [issueType, setIssueType] = useState('duplicate_title')
  const [file, setFile] = useState(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [originFilter, setOriginFilter] = useState('')
  const [enqueueLimit, setEnqueueLimit] = useState(25)
  const [legacyOnly, setLegacyOnly] = useState(true)

  const { data: batches, isLoading: batchesLoading, isFetching: batchesFetching } = useQuery(
    ['webmasterAuditBatches'],
    () => webmasterAuditAPI.getBatches(30),
    {
      refetchInterval: 15000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const { data: systemStatus } = useQuery(
    ['systemStatusWebmasterAudit'],
    statusAPI.getSystem,
    {
      refetchInterval: 15000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  )

  useEffect(() => {
    if (!selectedBatchId && batches?.length) {
      setSelectedBatchId(batches[0].id)
    }
  }, [batches, selectedBatchId])

  const { data: batchDetail, isLoading: detailLoading, isFetching: detailFetching } = useQuery(
    ['webmasterAuditBatch', selectedBatchId],
    () => webmasterAuditAPI.getBatch(selectedBatchId, 300),
    {
      enabled: Boolean(selectedBatchId),
      refetchInterval: 15000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const { data: filteredRows, isLoading: rowsLoading, isFetching: rowsFetching } = useQuery(
    ['webmasterAuditRows', selectedBatchId, statusFilter, originFilter],
    () => webmasterAuditAPI.getRows({
      batch_id: selectedBatchId,
      resolution_status: statusFilter || undefined,
      content_origin: originFilter || undefined,
      limit: 300
    }),
    {
      enabled: Boolean(selectedBatchId) && Boolean(statusFilter || originFilter),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const activeRows = statusFilter ? filteredRows || [] : batchDetail?.rows || []

  const uploadMutation = useMutation(
    () => webmasterAuditAPI.uploadCsv({ file, issue_type: issueType, source_site: 'lojs' }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['webmasterAuditBatches'])
        queryClient.invalidateQueries(['webmasterAuditBatch'])
        setSelectedBatchId(data.batch.id)
        setFile(null)
        addToast('CSV успешно загружен')
      },
      onError: (error) => {
        addToast(error.response?.data?.detail || 'Не удалось загрузить CSV', 'error')
      }
    }
  )

  const refreshMutation = useMutation(
    (onlyUnresolved) => webmasterAuditAPI.refreshBatch(selectedBatchId, onlyUnresolved),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['webmasterAuditBatches'])
        queryClient.invalidateQueries(['webmasterAuditBatch', selectedBatchId])
        queryClient.invalidateQueries(['webmasterAuditRows', selectedBatchId])
        addToast('Статусы пакета обновлены')
      },
      onError: (error) => {
        addToast(error.response?.data?.detail || 'Не удалось обновить пакет', 'error')
      }
    }
  )

  const enqueueMutation = useMutation(
    (overrideLimit) => webmasterAuditAPI.enqueueRegeneration(selectedBatchId, {
      resolution_status: 'needs_regeneration',
      limit: overrideLimit || enqueueLimit,
      content_origin: legacyOnly ? 'legacy_local' : undefined,
    }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['webmasterAuditBatches'])
        queryClient.invalidateQueries(['webmasterAuditBatch', selectedBatchId])
        queryClient.invalidateQueries(['webmasterAuditRows', selectedBatchId])
        addToast(`Поставлено в SEO-очередь: ${data.enqueued_count || 0}`)
      },
      onError: (error) => {
        addToast(error.response?.data?.detail || 'Не удалось поставить страницы в очередь', 'error')
      }
    }
  )

  const statusCards = useMemo(() => {
    const counts = batchDetail?.batch?.status_counts || {}
    return Object.keys(STATUS_META).map((status) => (
      <StatusBadge key={status} status={status} count={counts[status] || 0} />
    ))
  }, [batchDetail])

  const clusterPreview = batchDetail?.clusters || []
  const originCounts = batchDetail?.batch?.origin_counts || {}
  const aiStatusCounts = batchDetail?.batch?.seo_ai_status_counts || {}
  const operationalStats = useMemo(() => {
    const counts = batchDetail?.batch?.status_counts || {}
    const remaining =
      (counts.needs_regeneration || 0) +
      (counts.matched_pending || 0) +
      (counts.matched_failed || 0) +
      (counts.matched_unknown || 0)
    return {
      remaining,
      readyForWave: counts.needs_regeneration || 0,
      pending: counts.matched_pending || 0,
      failed: counts.matched_failed || 0,
      unresolvedNotFound: counts.not_found || 0,
    }
  }, [batchDetail])

  const readyByOrigin = useMemo(() => {
    const rows = batchDetail?.rows || []
    return rows.reduce((acc, row) => {
      if (row.resolution_status !== 'needs_regeneration') return acc
      const key = row.lojs_origin || 'none'
      acc[key] = (acc[key] || 0) + 1
      return acc
    }, {})
  }, [batchDetail])

  const autoWave = systemStatus?.scheduler?.webmaster_audit || null
  const autoWaveLabel = {
    enqueued: 'Поставлено в очередь',
    cooldown: 'Пауза',
    no_targets: 'Нет целей',
    disabled: 'Выключено',
    idle: 'Ожидание',
  }[autoWave?.action || autoWave?.reason] || (autoWave?.action || autoWave?.reason || 'Неизвестно')

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Аудит Вебмастера</h1>
          <p className="opacity-60 mt-1">Импорт CSV из Яндекс.Вебмастера и контроль того, какие URL уже найдены, обработаны и поставлены в SEO-очередь.</p>
          {(batchesFetching || detailFetching || rowsFetching) && (
            <p className="text-sm opacity-50 mt-2">Данные обновляются в фоне...</p>
          )}
        </div>
        {selectedBatchId && (
          <div className="flex gap-2">
            <div className="flex items-center gap-2 glass px-3 py-2 rounded-lg">
              <span className="text-sm opacity-70">Размер волны</span>
              <input
                type="number"
                min="1"
                max="200"
                value={enqueueLimit}
                onChange={(event) => setEnqueueLimit(Math.max(1, Math.min(200, Number(event.target.value) || 1)))}
                className="w-20 bg-transparent border border-white/10 rounded px-2 py-1"
              />
            </div>
            <label className="flex items-center gap-2 glass px-3 py-2 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={legacyOnly}
                onChange={(event) => setLegacyOnly(event.target.checked)}
              />
              <span className="text-sm opacity-80">только legacy</span>
            </label>
            {[5, 25, 50].map((size) => (
              <button
                key={size}
                onClick={() => {
                  setEnqueueLimit(size)
                  enqueueMutation.mutate(size)
                }}
                className="btn-ghost"
                disabled={enqueueMutation.isLoading}
              >
                Волна {size}
              </button>
            ))}
            <button
              onClick={() => enqueueMutation.mutate()}
              className="btn-primary"
              disabled={enqueueMutation.isLoading}
            >
              <Wand2 size={16} className={enqueueMutation.isLoading ? 'animate-pulse' : ''} />
              <span className="ml-2">{enqueueMutation.isLoading ? 'Ставлю в очередь...' : 'Запустить SEO-волну'}</span>
            </button>
            <button
              onClick={() => refreshMutation.mutate(true)}
              className="btn-ghost"
              disabled={refreshMutation.isLoading}
            >
              <RefreshCw size={16} className={refreshMutation.isLoading ? 'animate-spin' : ''} />
              <span className="ml-2">Обновить нерешённые</span>
            </button>
            <button
              onClick={() => refreshMutation.mutate(false)}
              className="btn-ghost"
              disabled={refreshMutation.isLoading}
            >
              <RefreshCw size={16} className={refreshMutation.isLoading ? 'animate-spin' : ''} />
              <span className="ml-2">Обновить всё</span>
            </button>
          </div>
        )}
      </div>

      <div className="glass p-5">
        <div className="flex flex-col lg:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm opacity-70 mb-2">Тип проблемы</label>
            <select
              value={issueType}
              onChange={(event) => setIssueType(event.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3"
            >
              {ISSUE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="flex-[2]">
            <label className="block text-sm opacity-70 mb-2">CSV-файл</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3"
            />
          </div>
          <button
            onClick={() => uploadMutation.mutate()}
            disabled={!file || uploadMutation.isLoading}
            className="btn-primary"
          >
            <Upload size={16} />
            <span className="ml-2">{uploadMutation.isLoading ? 'Загружаю...' : 'Загрузить CSV'}</span>
          </button>
        </div>
        <div className="mt-3 text-sm opacity-60">
          Формат CSV из Яндекс.Вебмастера не различает проблему сам по себе.
          Для работы этого экрана важнее всего сами URL и их текущий статус на сайте: найдено, в очереди, готово или требует новой регенерации.
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-6">
        <div className="space-y-4">
          <div className="glass p-4">
            <div className="font-semibold mb-3">Загрузки</div>
            <div className="space-y-3 max-h-[70vh] overflow-auto">
              {batchesLoading && !batches?.length ? (
                <div className="opacity-60">Загружаю пакеты...</div>
              ) : !batches?.length ? (
                <div className="opacity-60">Пакетов пока нет</div>
              ) : (
                batches.map((batch) => (
                  <BatchRow
                    key={batch.id}
                    batch={batch}
                    isActive={batch.id === selectedBatchId}
                    onSelect={setSelectedBatchId}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {!selectedBatchId ? (
            <div className="glass p-6 opacity-70">Выберите пакет слева, чтобы посмотреть детали.</div>
          ) : detailLoading && !batchDetail ? (
            <div className="glass p-6 opacity-70">Загружаю детали пакета...</div>
          ) : (
            <>
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="glass p-4">
                  <div className="text-sm opacity-60 mb-2">Пакет URL</div>
                  <div className="font-semibold">#{batchDetail?.batch?.id}</div>
                  <div className="text-sm opacity-60 mt-1">{batchDetail?.batch?.uploaded_filename}</div>
                  <div className="text-xs opacity-45 mt-2">
                    Источник отчёта: {ISSUE_LABELS[batchDetail?.batch?.issue_type] || batchDetail?.batch?.issue_type}
                  </div>
                </div>
                <div className="glass p-4">
                  <div className="text-sm opacity-60 mb-2">Всего URL</div>
                  <div className="text-2xl font-bold">{batchDetail?.batch?.total_urls || 0}</div>
                </div>
                <div className="glass p-4">
                  <div className="text-sm opacity-60 mb-2">Группы из CSV</div>
                  <div className="text-2xl font-bold">{batchDetail?.batch?.total_clusters || 0}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
                {statusCards}
              </div>

              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="glass p-4">
                  <div className="text-sm opacity-60 mb-2">URL ещё в работе</div>
                  <div className="text-2xl font-bold text-orange-300">{operationalStats.remaining}</div>
                  <div className="text-xs opacity-60 mt-1">очередь + ошибки + повторная регенерация</div>
                </div>
                <div className="glass p-4">
                  <div className="text-sm opacity-60 mb-2">Готово к новой волне</div>
                  <div className="text-2xl font-bold text-cyan-300">{operationalStats.readyForWave}</div>
                  <div className="text-xs opacity-60 mt-1">URL, которым нужен новый SEO-прогон</div>
                </div>
                <div className="glass p-4">
                  <div className="text-sm opacity-60 mb-2">Legacy готовы</div>
                  <div className="text-2xl font-bold text-violet-300">{readyByOrigin.legacy_local || 0}</div>
                  <div className="text-xs opacity-60 mt-1">старые локальные URL сайта</div>
                </div>
                <div className="glass p-4">
                  <div className="text-sm opacity-60 mb-2">Агрегатор готовы</div>
                  <div className="text-2xl font-bold text-blue-300">{readyByOrigin.aggregator_external || 0}</div>
                  <div className="text-xs opacity-60 mt-1">импортированные URL из агрегатора</div>
                </div>
              </div>

              <div className="glass p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock3 size={18} className="text-cyan-300" />
                  <div className="font-semibold">Автоволна</div>
                </div>
                {autoWave ? (
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="opacity-60">Состояние</div>
                      <div className="font-medium mt-1">{autoWaveLabel}</div>
                    </div>
                    <div>
                      <div className="opacity-60">Интервал</div>
                      <div className="font-medium mt-1">{autoWave.interval_minutes || '—'} мин</div>
                    </div>
                    <div>
                      <div className="opacity-60">Лимит волны</div>
                      <div className="font-medium mt-1">{autoWave.wave_limit || '—'}</div>
                    </div>
                    <div>
                      <div className="opacity-60">Последний запуск</div>
                      <div className="font-medium mt-1">
                        {autoWave.ran_at ? new Date(autoWave.ran_at).toLocaleString() : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="opacity-60">Пакет</div>
                      <div className="font-medium mt-1">#{autoWave.batch_id || '—'}</div>
                    </div>
                    <div>
                      <div className="opacity-60">Запрошено</div>
                      <div className="font-medium mt-1">{autoWave.requested || 0}</div>
                    </div>
                    <div>
                      <div className="opacity-60">Поставлено в очередь</div>
                      <div className="font-medium mt-1">{autoWave.enqueued_count || 0}</div>
                    </div>
                    <div>
                      <div className="opacity-60">Режим</div>
                      <div className="font-medium mt-1">{autoWave.only_legacy ? 'только legacy' : 'все источники'}</div>
                    </div>
                  </div>
                ) : (
                  <div className="opacity-60 text-sm">Статус автоволны пока не появился</div>
                )}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="glass p-4">
                  <div className="font-semibold mb-3">Источники</div>
                  <div className="space-y-2 text-sm">
                    {Object.entries(originCounts).map(([origin, count]) => (
                      <div key={origin} className="flex items-center justify-between">
                        <span className="opacity-70">{ORIGIN_LABELS[origin] || origin}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                    {!Object.keys(originCounts).length && <div className="opacity-50">Нет данных по источникам</div>}
                  </div>
                </div>
                <div className="glass p-4">
                  <div className="font-semibold mb-3">Статус AI</div>
                  <div className="space-y-2 text-sm">
                    {Object.entries(aiStatusCounts).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="opacity-70">{AI_STATUS_LABELS[status] || status}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                    {!Object.keys(aiStatusCounts).length && <div className="opacity-50">Нет данных по AI-статусам</div>}
                  </div>
                </div>
              </div>

              <div className="glass p-4 overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="font-semibold">Кластеры дублей</div>
                    <div className="text-sm opacity-60">Группы URL из CSV и их текущий статус обработки на сайте.</div>
                  </div>
                </div>
                <div className="overflow-auto">
                  <table className="table-dark">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Метка</th>
                        <th>Размер CSV</th>
                        <th>Страниц</th>
                        <th>Готово</th>
                        <th>В очереди</th>
                        <th>На регенерацию</th>
                        <th>Не найдено</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clusterPreview.map((cluster) => (
                        <tr key={`${cluster.cluster_index}-${cluster.cluster_label}`}>
                          <td className="font-mono text-sm">{cluster.cluster_index || '—'}</td>
                          <td className="max-w-[420px] truncate" title={cluster.cluster_label}>{cluster.cluster_label || '—'}</td>
                          <td>{cluster.cluster_size || '—'}</td>
                          <td>{cluster.rows_count}</td>
                          <td>{cluster.status_counts?.matched_done || 0}</td>
                          <td>{cluster.status_counts?.matched_pending || 0}</td>
                          <td>{cluster.status_counts?.needs_regeneration || 0}</td>
                          <td>{cluster.status_counts?.not_found || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="glass p-4 overflow-hidden">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div>
                    <div className="font-semibold">Страницы</div>
                    <div className="text-sm opacity-60">Конкретные URL из CSV и их фактический статус на сайте.</div>
                  </div>
                  <div className="flex gap-3">
                    <select
                      value={originFilter}
                      onChange={(event) => setOriginFilter(event.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2"
                    >
                      <option value="">Все источники</option>
                      {Object.entries(ORIGIN_LABELS).map(([origin, label]) => (
                        <option key={origin} value={origin}>{label}</option>
                      ))}
                    </select>
                    <select
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2"
                    >
                      <option value="">Все статусы</option>
                      {Object.entries(STATUS_META).map(([status, meta]) => (
                        <option key={status} value={status}>{meta.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="overflow-auto">
                  <table className="table-dark">
                    <thead>
                      <tr>
                        <th>Статус</th>
                        <th>URL страницы</th>
                        <th>Источник CSV</th>
                        <th>Источник</th>
                        <th>AI</th>
                        <th>Кластер</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rowsLoading ? [] : activeRows).map((row) => (
                        <tr key={row.id}>
                          <td>{STATUS_META[row.resolution_status]?.label || row.resolution_status}</td>
                          <td className="max-w-[360px]">
                            {row.matched_public_url ? (
                              <a
                                href={buildLojsUrl(row.matched_public_url)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-cyan-300 hover:underline break-all"
                              >
                                {buildLojsUrl(row.matched_public_url)}
                              </a>
                            ) : (
                              <span className="opacity-50">—</span>
                            )}
                          </td>
                          <td className="max-w-[360px] break-all">
                            <div className="opacity-70">{row.raw_url || row.normalized_path || '—'}</div>
                          </td>
                          <td>{ORIGIN_LABELS[row.lojs_origin] || row.lojs_origin || '—'}</td>
                          <td>{AI_STATUS_LABELS[row.seo_ai_status] || row.seo_ai_status || '—'}</td>
                          <td className="max-w-[260px] truncate" title={row.cluster_label}>{row.cluster_label || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rowsLoading && !activeRows?.length && <div className="p-4 opacity-60">Загружаю отфильтрованные строки...</div>}
                  {!rowsLoading && !activeRows?.length && <div className="p-4 opacity-60">Для этого фильтра строк нет.</div>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
